<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApiClient, formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import ProductPickerModal from './ProductPickerModal.vue'
import { useFinanceApi } from '../composables/useFinanceApi'
import { newDraft, draftPayload, type CostVersion } from '../utils/productCostWorkspace'
import type { ProductOption, CurrencyOption } from '../types/finance'
const props=defineProps<{mode:'create'|'edit'|'clone';version?:CostVersion|null}>()
const emit=defineEmits<{saved:[version:CostVersion];cancel:[]}>()
const { t }=useI18n()
const client=useApiClient()
const auth=useAuthStore()
const { listCurrencies }=useFinanceApi()
const draft=ref(newDraft(props.version))
const busy=ref(false), loading=ref(false), error=ref(''), picker=ref(false), referenceError=ref('')
const productName=ref(String(props.version?.product_name ?? ''))
const currencies=ref<CurrencyOption[]>([])
const boms=ref<{id:number;version_number:number;is_active:number;description?:string}[]>([])
const title=computed(()=>props.mode==='edit'?t('finance.productCost.editor.titleEdit'):props.mode==='clone'?t('finance.productCost.editor.titleClone'):t('finance.productCost.editor.titleCreate'))
const productLabel=computed(()=>productName.value || t('finance.productCost.editor.selectProduct'))
const maySelect=computed(()=>auth.user?.permissions.includes('products.view') && auth.user?.permissions.includes('bom.view'))
const keyCache=new Map<string,string>()
const inputFields=['scenario_name','production_type','costing_date','effective_from','effective_to','batch_quantity','expected_output_quantity','yield_rate'] as const
const fieldLabelKeys:Record<string,string>={
  scenario_name:'scenarioName',production_type:'productionType',costing_date:'costingDate',
  effective_from:'effectiveFrom',effective_to:'effectiveTo',batch_quantity:'batchQuantity',
  expected_output_quantity:'expectedOutputQuantity',yield_rate:'yieldRate',
}
const optional=['scenario_name','effective_from','effective_to']
const fieldLabel=(field:string)=>t(`finance.productCost.editor.${fieldLabelKeys[field]}`)
function type(field:string) { return field.includes('date') || field.startsWith('effective_') ? 'date' : 'text' }
async function loadBoms() {
  boms.value=[]
  if(!draft.value.product_id) return
  const result=await client.get<{data:typeof boms.value}>('/bom',{params:{product_id:draft.value.product_id,per_page:100,page:1}})
  boms.value=result.data.data
}
async function choose(product:ProductOption) {
  picker.value=false; loading.value=true; referenceError.value=''
  draft.value.product_id=String(product.id); draft.value.bom_version_id=''
  productName.value=product.name+(product.sku?' · '+product.sku:'')
  try {
    await loadBoms()
    const active=boms.value.find(bom=>Number(bom.is_active)===1)
    if(active) draft.value.bom_version_id=String(active.id)
  } catch(cause) { referenceError.value=formatApiError(cause) } finally {loading.value=false}
}
async function initialize() {
  loading.value=true; referenceError.value=''
  try {
    currencies.value=await listCurrencies()
    if(!draft.value.costing_currency_id) draft.value.costing_currency_id=String(currencies.value.find(c=>Number(c.is_base)===1)?.id ?? '')
    await loadBoms()
  } catch(cause) { referenceError.value=formatApiError(cause) } finally {loading.value=false}
}
async function save() {
  if(busy.value || loading.value) return
  error.value=''
  if(!draft.value.product_id || !boms.value.some(b=>String(b.id)===draft.value.bom_version_id)) { error.value=t('finance.productCost.editor.productBomRequired');return }
  if(!currencies.value.some(c=>String(c.id)===draft.value.costing_currency_id)) { error.value=t('finance.productCost.editor.currencyRequired');return }
  for(const field of ['batch_quantity','expected_output_quantity','yield_rate']) {
    const value=draft.value[field]
    if(!/^\d+(\.\d{1,8})?$/.test(value) || Number(value)<=0) { error.value=t('finance.productCost.editor.positiveNumbers'); return }
  }
  if(Number(draft.value.yield_rate)>1) { error.value=t('finance.productCost.editor.yieldMax');return }
  if(draft.value.effective_to && (!draft.value.effective_from || draft.value.effective_to<draft.value.effective_from)) { error.value=t('finance.productCost.editor.periodInvalid');return }
  busy.value=true
  try {
    const payload=draftPayload(draft.value)
    let version:CostVersion
    if(props.mode==='edit' && props.version) {
      version=(await client.put<{data:CostVersion}>(`/product-costs/${props.version.id}`,{...payload,row_version:props.version.row_version})).data.data
    } else {
      // Clone is a new draft of assumptions; calculation lines are deliberately recollected.
      const fingerprint=JSON.stringify(payload)
      const key=keyCache.get(fingerprint)??crypto.randomUUID();keyCache.set(fingerprint,key)
      version=(await client.post<{data:CostVersion}>('/product-costs',payload,{headers:{'Idempotency-Key':key}})).data.data
      keyCache.delete(fingerprint)
    }
    emit('saved',version)
  } catch(cause) { error.value=formatApiError(cause) } finally {busy.value=false}
}
onMounted(initialize)
</script>
<template>
  <section class="form-card cost-draft-editor" :aria-busy="busy || loading">
    <h3>{{ title }}</h3>
    <p v-if="mode==='clone'">{{ t('finance.productCost.editor.cloneNote') }}</p>
    <p v-if="!maySelect">{{ t('finance.productCost.editor.permissions') }}</p>
    <p v-if="referenceError" role="alert" class="notice error">{{ referenceError }} <button class="btn-secondary" :disabled="loading" @click="initialize">{{ t('finance.productCost.editor.retryLoad') }}</button></p>
    <p v-if="error" role="alert" class="notice error">{{ error }}</p>
    <form @submit.prevent="save">
      <fieldset :disabled="busy || loading">
        <div class="form-grid">
          <label class="field">{{ t('finance.productCost.editor.product') }} <button class="field-button" type="button" :disabled="mode==='edit' || !maySelect" @click="picker=true">{{ productLabel }}</button></label>
          <label class="field">{{ t('finance.productCost.editor.bomVersion') }} <select v-model="draft.bom_version_id" required><option value="" disabled>{{ t('finance.productCost.editor.selectBom') }}</option><option v-for="bom in boms" :key="bom.id" :value="String(bom.id)">v{{ bom.version_number }} · {{ Number(bom.is_active)===1 ? t('finance.productCost.editor.active') : t('finance.productCost.editor.inactive') }} {{ bom.description }}</option></select></label>
          <label class="field">{{ t('finance.productCost.editor.currency') }} <select v-model="draft.costing_currency_id" required><option value="" disabled>{{ t('finance.productCost.editor.selectCurrency') }}</option><option v-for="currency in currencies" :key="currency.id" :value="String(currency.id)">{{ currency.code }} — {{ currency.name }}</option></select></label>
          <label class="field" v-for="field in inputFields" :key="field">{{ fieldLabel(field) }}<input v-model="draft[field]" :type="type(field)" :required="!optional.includes(field)"></label>
          <label class="field">{{ t('finance.productCost.editor.costType') }} <select v-model="draft.cost_type"><option value="standard">{{ t('finance.productCost.editor.costTypeStandard') }}</option><option value="planned">{{ t('finance.productCost.editor.costTypePlanned') }}</option></select></label>
          <label class="field">{{ t('finance.productCost.editor.shift') }} <select v-model="draft.shift"><option value="day">{{ t('finance.productCost.editor.shiftDay') }}</option><option value="night">{{ t('finance.productCost.editor.shiftNight') }}</option><option value="blended">{{ t('finance.productCost.editor.shiftBlended') }}</option></select></label>
          <label class="field">{{ t('finance.productCost.editor.notes') }} <textarea v-model="draft.notes" /></label>
        </div>
        <p v-if="draft.product_id && !boms.length && !referenceError">{{ t('finance.productCost.editor.noBoms') }}</p>
        <div class="form-actions"><button class="btn-primary" :disabled="!maySelect || !!referenceError">{{ t('finance.productCost.editor.save') }}</button>
        <button class="btn-secondary" type="button" @click="emit('cancel')">{{ t('finance.productCost.editor.cancel') }}</button></div>
      </fieldset>
    </form>
    <ProductPickerModal :open="picker" :selected-id="draft.product_id" @close="picker=false" @select="choose" />
  </section>
</template>
<style scoped>
.cost-draft-editor { margin-bottom: 20px; }
fieldset { border: 0; padding: 0; min-width: 0; }
.form-grid { margin-bottom: 16px; }
.field { min-width: 0; color: #4c5a4f; font-size: 13px; font-weight: 700; }
.field input, .field select, .field textarea { min-width: 0; width: 100%; box-sizing: border-box; }
</style>
